/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-05-25 22:25:50
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Card } from '@shopify/polaris';
import { Field } from 'redux-form';
import validateColor from 'validate-color';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import NoSwatch from './no-swatch';
import LoadingRows from './loading-rows';
import TableHeadingCellWithSort from './table-heading-cell-with-sort';
import RenderColorCodesSelect from './render-color-codes-select';
import RenderImageSelect from './render-image-select';
import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { OPTION_DISPLAY_TYPES } from 'src/constants/swatch-settings';
import NoColor from 'src/assets/images/styles/noColor.png';

type Props = {
  list: Array,
  loading: boolean,
  success: boolean
};

type ComposedProps = Props & WithI18nProps;

class ListSwatches extends Component<ComposedProps> {
  render() {
    const {
      i18n,
      fields,
      list,
      loading,
      isAsc,
      sortId,
      updateSwatchField,
      isSearchResult,
      swatchSettingsError
    } = this.props;

    const optionDisplayTypes = OPTION_DISPLAY_TYPES.map(function (item) {
      return {
        ...item,
        label: i18n.translate(item.label)
      };
    });
    return (
      <>
        <Card.Section>
          <div className="Polaris-DataTable">
            <div className="Polaris-DataTable__ScrollContainer" style={{ marginLeft: 0 }}>
              <table className="Polaris-DataTable__Table">
                <thead>
                  <tr>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header" />
                    <TableHeadingCellWithSort
                      heading={i18n.translate('AdminHTML.SwatchSettings.SwatchSettings.swatchName')}
                      isAsc={isAsc}
                      idSort={sortId}
                      id="name"
                      onSort={this.onSort}
                    />
                    <TableHeadingCellWithSort
                      heading={i18n.translate('AdminHTML.SwatchSettings.SwatchSettings.type')}
                      isAsc={isAsc}
                      idSort={sortId}
                      id="type"
                      onSort={this.onSort}
                    />

                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                      {i18n.translate('AdminHTML.SwatchSettings.SwatchSettings.swatchValue')}
                    </th>
                  </tr>
                </thead>
                {!_.isEmpty(list) ? (
                  <tbody>
                    {fields.map((item, key) => {
                      const swatch = list[key] || {};
                      const { name = '', type = '' } = swatch;
                      let colorValue = this.buildColorImage(swatch);

                      return (
                        // Enable drag for per option row
                        <tr className="Polaris-DataTable__TableRow" key={`swatch-${key}`}>
                          {/* Color */}
                          <td className="Polaris-DataTable__Cell">
                            <div className={`swatch-color swatch-${type}`} style={colorValue}></div>
                          </td>

                          {/* Name */}
                          <td className="Polaris-DataTable__Cell">{name}</td>

                          {/* Option display */}
                          <td className="Polaris-DataTable__Cell">
                            <Field
                              name={`${item}.type`}
                              options={optionDisplayTypes}
                              component={RenderSelect}
                              placeholder={i18n.translate(
                                'AdminHTML.SwatchSettings.SwatchSettings.type_Placeholder'
                              )}
                            />
                          </td>

                          {/* Value */}
                          <td className="Polaris-DataTable__Cell">
                            {type === 'image' ? (
                              <RenderImageSelect item={item} />
                            ) : (
                              <RenderColorCodesSelect
                                item={item}
                                itemKey={key}
                                swatch={swatch}
                                updateSwatchField={updateSwatchField}
                                errorMessage={
                                  swatchSettingsError[key] ? swatchSettingsError[key] : ''
                                }
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {loading && (
                      // Loading rows while reading swatch
                      <LoadingRows />
                    )}
                  </tbody>
                ) : (
                  <tbody>
                    {loading && (
                      // Loading rows while reading swatch
                      <LoadingRows />
                    )}
                  </tbody>
                )}
              </table>

              {/* No swatch card */}
              {_.isEmpty(list) && !loading ? <NoSwatch isSearchResult={isSearchResult} /> : ''}
            </div>
          </div>
        </Card.Section>
      </>
    );
  }

  // Build color before name
  buildColorImage = swatch => {
    const { name = '', type = '', colorCode01 = '', colorCode02 = '', imageUrl = '' } = swatch;

    //Check no color value
    if ((type === 'image' && _.isEmpty(imageUrl)) || (type !== 'image' && colorCode01 === '')) {
      if (validateColor(name.toLowerCase())) {
        return { backgroundColor: name.toLowerCase() };
      } else {
        return { background: `url("${NoColor}") center center / cover no-repeat `, border: 'none' };
      }
    }
    switch (type) {
      case 'oneColor': {
        return { backgroundColor: colorCode01 };
      }
      case 'twoColors': {
        return {
          backgroundImage: `-webkit-linear-gradient(135deg, ${colorCode02} 50%, ${colorCode01} 50%)`
        };
      }
      case 'image': {
        return { background: `url("${imageUrl}") center center / cover no-repeat ` };
      }
      default:
        if (validateColor(name.toLowerCase())) {
          return { backgroundColor: name.toLowerCase() };
        } else {
          return {
            background: `url("${NoColor}") center center / cover no-repeat `,
            border: 'none'
          };
        }
    }
  };

  // Handle click sort
  onSort = (sortId, isAsc) => {
    const { onSort } = this.props;

    onSort(sortId, isAsc);
  };
}

export default withI18n()(ListSwatches);
